import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Alert, CircularProgress, Snackbar, Drawer, AppBar, Toolbar, IconButton, List, ListItem, ListItemIcon, ListItemText, Divider, Button } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import StorageIcon from '@mui/icons-material/Storage';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SecurityIcon from '@mui/icons-material/Security';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import { TenantManagement } from './TenantManagement';
import Resources from './Resources';
import VMAssignmentManagement from './VMAssignmentManagement';
import { MFASettings } from './MFASettings';

interface DashboardStats {
  user_role: 'admin' | 'tenant';
  total_companies?: number;
  total_vms: number;
  total_assignments?: number;
  running_vms: number;
  stopped_vms: number;
  company_name?: string;
  resources: {
    cpu: {
      total_cores: number;
      usage_percent: number;
    };
    memory: {
      total_gb: number;
      used_gb: number;
      usage_percent: number;
    };
    disk: {
      total_gb: number;
      used_gb: number;
      usage_percent: number;
    };
  };
  vm_distribution?: Array<{ company: string; count: number }>;
  recent_vms?: Array<{
    vmid: number;
    name: string;
    status: string;
    node: string;
    cpu_percent: number;
    memory_percent: number;
  }>;
  vms?: Array<{
    vmid: number;
    name: string;
    status: string;
    node: string;
    cpu_percent: number;
    memory_percent: number;
    uptime_hours: number;
  }>;
  recent_activities?: Array<{
    vmid: number;
    vm_name: string;
    node: string;
    type: string;
    status: string;
    starttime: number;
    endtime?: number;
    user: string;
  }>;
}

export const Dashboard: React.FC<{ user: any; mode?: 'light' | 'dark'; setMode?: (m: 'light' | 'dark') => void; onLogout?: () => void }> = ({ user, mode, setMode, onLogout }) => {
  const [currentPage, setCurrentPage] = useState<'dashboard' | 'tenants' | 'resources' | 'vm-assignments' | 'security'>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [mfaStatus, setMfaStatus] = useState<{ enabled: boolean; type?: string } | null>(null);
  const [mfaCheckLoading, setMfaCheckLoading] = useState(true);
  const drawerWidth = 220;

  // Check MFA status on mount
  useEffect(() => {
    const checkMFAStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/mfa/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setMfaStatus(data);
          // Force user to security page if MFA not enabled
          if (!data.enabled) {
            setCurrentPage('security');
          }
        }
      } catch (err) {
        console.error('Failed to check MFA status:', err);
      } finally {
        setMfaCheckLoading(false);
      }
    };
    checkMFAStatus();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) throw new Error('Failed to fetch stats');
        const data = await response.json();
        setStats(data);
      } catch (err: any) {
        setError(err.message || 'Error loading dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: 'inherit' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box', background: 'inherit', borderRight: 0 },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', pt: 2 }}>
          {/* ZYNEX Branding in Sidebar */}
          <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: 4,
                background: 'linear-gradient(135deg, #2d7a5f 0%, #1e5545 50%, #4facfe 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              ZYNEX
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <List>
            <ListItem 
              button 
              selected={currentPage === 'dashboard'} 
              onClick={() => mfaStatus?.enabled && setCurrentPage('dashboard')}
              disabled={!mfaStatus?.enabled}
            >
              <ListItemIcon><DashboardIcon /></ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem 
              button 
              selected={currentPage === 'resources'} 
              onClick={() => mfaStatus?.enabled && setCurrentPage('resources')}
              disabled={!mfaStatus?.enabled}
            >
              <ListItemIcon><StorageIcon /></ListItemIcon>
              <ListItemText primary="Resources" />
            </ListItem>
            <ListItem button selected={currentPage === 'security'} onClick={() => setCurrentPage('security')}>
              <ListItemIcon><SecurityIcon color={!mfaStatus?.enabled ? 'error' : 'inherit'} /></ListItemIcon>
              <ListItemText primary="Security" />
            </ListItem>
            {user.is_admin && (
              <>
                <ListItem 
                  button 
                  selected={currentPage === 'tenants'} 
                  onClick={() => mfaStatus?.enabled && setCurrentPage('tenants')}
                  disabled={!mfaStatus?.enabled}
                >
                  <ListItemIcon><BusinessIcon /></ListItemIcon>
                  <ListItemText primary="Companies" />
                </ListItem>
                <ListItem 
                  button 
                  selected={currentPage === 'vm-assignments'} 
                  onClick={() => mfaStatus?.enabled && setCurrentPage('vm-assignments')}
                  disabled={!mfaStatus?.enabled}
                >
                  <ListItemIcon><AssignmentIcon /></ListItemIcon>
                  <ListItemText primary="VM Assignments" />
                </ListItem>
              </>
            )}
          </List>
          <Divider sx={{ my: 2 }} />
          <List>
            <ListItem button onClick={() => setMode && setMode(mode === 'light' ? 'dark' : 'light')}>
              <ListItemIcon>{mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}</ListItemIcon>
              <ListItemText primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'} />
            </ListItem>
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 0, background: 'inherit' }}>
        <AppBar position="static" elevation={0} color="transparent" sx={{ borderBottom: 1, borderColor: 'divider', background: 'inherit' }}>
          <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
            <Typography 
              variant="h6" 
              sx={{ 
                flexGrow: 1, 
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Welcome, {user.username}!
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                flexGrow: 1, 
                fontWeight: 700,
                fontSize: '1rem',
                display: { xs: 'block', sm: 'none' }
              }}
            >
              {user.username}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mr: 2,
                display: { xs: 'none', md: 'block' }
              }}
            >
              Role: User | Tenant: {user.tenant_id || 'N/A'}
            </Typography>
            <Button 
              variant="outlined" 
              color="inherit" 
              startIcon={<LogoutIcon />} 
              onClick={onLogout} 
              sx={{ 
                ml: 2,
                display: { xs: 'none', sm: 'flex' }
              }}
            >
              Logout
            </Button>
            <IconButton 
              color="inherit" 
              onClick={onLogout} 
              sx={{ 
                display: { xs: 'flex', sm: 'none' }
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ mt: 4, maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 } }}>
          {/* MFA Setup Required Alert */}
          {!mfaCheckLoading && !mfaStatus?.enabled && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                🔒 Multi-Factor Authentication Required
              </Typography>
              <Typography variant="body2">
                For security purposes, you must set up MFA before accessing other features. Please complete the MFA setup below.
              </Typography>
            </Alert>
          )}
          
          {currentPage === 'dashboard' && (
            <>
              <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                Dashboard Overview
              </Typography>
              {loading && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}><CircularProgress aria-label="loading dashboard" /></Box>}
              {error && <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert>}
              
              {!loading && stats && (
                <Grid container spacing={3}>
                  {/* Main Stats Cards */}
                  <Grid item xs={6} sm={6} md={3}>
                    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3, textAlign: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
                      <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>{stats.total_vms}</Typography>
                      <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Total VMs</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3, textAlign: 'center', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' }}>
                      <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>{stats.running_vms}</Typography>
                      <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Running</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3, textAlign: 'center', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff' }}>
                      <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>{stats.stopped_vms}</Typography>
                      <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Stopped</Typography>
                    </Paper>
                  </Grid>
                  {stats.user_role === 'admin' && stats.total_companies !== undefined && (
                    <Grid item xs={6} sm={6} md={3}>
                      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3, textAlign: 'center', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#fff' }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>{stats.total_companies}</Typography>
                        <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Companies</Typography>
                      </Paper>
                    </Grid>
                  )}
                  {stats.user_role === 'tenant' && stats.company_name && (
                    <Grid item xs={6} sm={6} md={3}>
                      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3, textAlign: 'center', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#fff' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' } }}>{stats.company_name}</Typography>
                        <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Your Company</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Resource Usage Cards */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>CPU Usage</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>{stats.resources.cpu.usage_percent}%</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {stats.resources.cpu.total_cores} Total Cores
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>Memory Usage</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#f5576c', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>{stats.resources.memory.usage_percent}%</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {stats.resources.memory.used_gb.toFixed(2)} GB / {stats.resources.memory.total_gb.toFixed(2)} GB
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={12} md={4}>
                    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>Disk Usage</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#00f2fe', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>{stats.resources.disk.usage_percent}%</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {stats.resources.disk.used_gb.toFixed(2)} GB / {stats.resources.disk.total_gb.toFixed(2)} GB
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Admin: VM Distribution by Company */}
                  {stats.user_role === 'admin' && stats.vm_distribution && stats.vm_distribution.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>VM Distribution by Company</Typography>
                        <Box>
                          {stats.vm_distribution.map((item, idx) => (
                            <Box key={idx} sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">{item.company}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.count} VMs</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  )}

                  {/* Admin: Recent VMs */}
                  {stats.user_role === 'admin' && stats.recent_vms && stats.recent_vms.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Recent VMs</Typography>
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                          {stats.recent_vms.map((vm, idx) => (
                            <Box key={idx} sx={{ mb: 1.5, pb: 1.5, borderBottom: idx < stats.recent_vms!.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {vm.name} ({vm.vmid})
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Node: {vm.node} | Status: {vm.status} | CPU: {vm.cpu_percent}% | Memory: {vm.memory_percent}%
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  )}

                  {/* Tenant: VM List */}
                  {stats.user_role === 'tenant' && stats.vms && stats.vms.length > 0 && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Your Virtual Machines</Typography>
                        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                          {stats.vms.map((vm, idx) => (
                            <Box key={idx} sx={{ mb: 2, pb: 2, borderBottom: idx < stats.vms!.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {vm.name} ({vm.vmid})
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Node: {vm.node} | Status: <span style={{ color: vm.status === 'running' ? '#43e97b' : '#f5576c' }}>{vm.status}</span>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                CPU: {vm.cpu_percent}% | Memory: {vm.memory_percent}%
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  )}

                  {/* Recent Activity Logs */}
                  {stats.recent_activities && stats.recent_activities.length > 0 && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Recent Activity</Typography>
                        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                          {stats.recent_activities.map((activity, idx) => (
                            <Box key={idx} sx={{ mb: 1.5, pb: 1.5, borderBottom: idx < stats.recent_activities!.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {activity.type} - {activity.vm_name} ({activity.vmid})
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Node: {activity.node} | Status: <span style={{ color: activity.status === 'OK' ? '#43e97b' : activity.status === 'running' ? '#4facfe' : '#f5576c' }}>{activity.status}</span> | User: {activity.user}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {new Date(activity.starttime * 1000).toLocaleString()}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          )}
          {currentPage === 'resources' && <Resources token={localStorage.getItem('token') || ''} userRole={stats?.user_role} />}
          {currentPage === 'security' && (
            <MFASettings 
              token={localStorage.getItem('token') || ''} 
              onMFAEnabled={() => {
                setMfaStatus({ enabled: true });
                setCurrentPage('dashboard');
              }}
            />
          )}
          {currentPage === 'tenants' && <TenantManagement token={localStorage.getItem('token') || ''} />}
          {currentPage === 'vm-assignments' && <VMAssignmentManagement />}
          <Snackbar
            open={!!successMsg}
            autoHideDuration={2000}
            onClose={() => setSuccessMsg(null)}
            message={successMsg}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />
        </Box>
      </Box>
    </Box>
  );
};
